import {
  getFirstFiniteNumber,
  getFirstPriceString,
  resolveCurrencyCode,
  toFiniteNumber,
} from '@/shared/utils/price';

export interface ProductImage {
  id: string;
  image: string;
  alt_text: string;
  order: number;
}

export interface RefurbishedInfo {
  grade: 'A' | 'B' | 'C';
  condition: string;
  warrantyMonths?: number;
  warranty_months?: number;
  includesAccessories?: boolean;
  includes_accessories?: boolean;
}

export interface PssOptions {
  available: boolean;
  as_low_as_daily?: number;
  as_low_as_weekly?: number;
  as_low_as_monthly?: number;
  popular_plan_months?: number;
}

export interface SellabilityStatus {
  state: 'sellable' | 'unsellable';
  primary_reason_code?: string;
}

export interface VariantSummary {
  count: number;
  min_price?: string | null;
  max_price?: string | null;
  default_variant_id?: string | null;
}

export interface ProductSpecification {
  display_name: string;
  value: string;
  category?: string | null;
}

export interface ProductExpertAdvice {
  advice_text?: string | null;
  top_pick?: boolean | null;
  perfect_for?: string[];
  not_ideal_for?: string[];
  pro_tip?: string | null;
}

export interface ProductSocialProof {
  view_count?: number | null;
  like_count?: number | null;
  inquiry_count?: number | null;
}

export interface ProductVariant {
  id: string;
  variant_attributes?: Record<string, string> | null;
  pricing?: {
    display_price?: string | null;
    current_display_price?: string | null;
    currency?: string | null;
  };
  pss_options?: PssOptions;
  sellability?: SellabilityStatus;
  image_url?: string | null;
  tags?: string[];
  refurbished_info?: RefurbishedInfo;
  stock_status?: string;
  quantity_available?: number | null;
}

export class Product {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly search_name: string,
    public readonly brand_name: string,
    public readonly search_brand_name: string,
    public readonly model_no: string,
    public readonly description: string,
    public readonly rich_description: string,
    public readonly cost_price: number,
    public readonly retail_price: number,
    public readonly max_payment_period: number,
    public readonly images: ProductImage[],
    public readonly image_url: string | null,
    public readonly short_url: string | null,
    public readonly category: { id: string; name: string, slug:string },
    public readonly merchant_shop_id: string,
    public readonly shop_name: string,
    public readonly shop_logo: string,
    public readonly lender_id: string,
    public readonly is_featured: boolean,
    public readonly status: ProductStatus,
    public readonly postgres_id: string,
    public readonly created_time: Date,
    // New fields based on API documentation
    public readonly tags: string[] = [],
    public readonly pricing: ProductPricing,
    public readonly rating_average?: number,
    public readonly compare_at_price?: number,
    public readonly refurbished_info?: RefurbishedInfo,
    public readonly pss_options?: PssOptions,
    public readonly currency: string = 'GHS',
    public readonly variant_summary?: VariantSummary | null,
    public readonly variants: ProductVariant[] = [],
    public readonly is_master_product: boolean = false,
    public readonly variant_attributes?: Record<string, string> | null,
    public readonly stock_status?: string,
    public readonly quantity_available?: number | null,
    public readonly sellability?: SellabilityStatus,
    public readonly specifications: ProductSpecification[] = [],
    public readonly expert_advice?: ProductExpertAdvice | null,
    public readonly social_proof?: ProductSocialProof | null,
    public readonly is_favorite: boolean = false
  ) {}

  /**
   * Create a Product from a plain object that has the same fields as the Product class.
   * Useful for cloning or constructing from deserialized data already normalized to domain fields.
   */
  static fromObject(obj: {
    // This repo is mid-migration from legacy flat price fields (`retail_price`) to
    // the canonical nested pricing contract (`pricing.display_price`). Consumers
    // can send either shape depending on deploy order. Treat the input as a
    // tolerant, best-effort payload and normalize it here.
    id: string;
    name: string;
  } & Partial<ApiProductResponse> & Record<string, any>): Product {
    const pricingCurrency = resolveCurrencyCode(
      obj.pricing?.currency,
      obj.currency
    );
    const legacyPriceValue = getFirstPriceString(
      obj.retail_price,
      obj.display_price,
      obj.current_display_price
    );
    const normalizedPricing: ProductPricing = {
      display_price: getFirstPriceString(
        obj.pricing?.display_price,
        obj.pricing?.current_display_price,
        legacyPriceValue
      ),
      current_display_price: getFirstPriceString(
        obj.pricing?.current_display_price,
        obj.pricing?.display_price,
        legacyPriceValue
      ),
      currency: pricingCurrency,
      original_price: obj.pricing?.original_price,
      is_discounted: obj.pricing?.is_discounted ?? false,
      discount: obj.pricing?.discount,
    };

    const derivedRetailPrice =
      (obj.pricing != null
        ? getFirstFiniteNumber(
            obj.pricing?.current_display_price,
            obj.pricing?.display_price,
            obj.retail_price,
            obj.current_display_price,
            obj.display_price
          )
        : getFirstFiniteNumber(
            obj.retail_price,
            obj.current_display_price,
            obj.display_price
          )) ?? 0;

    const derivedCostPrice = toFiniteNumber(obj.cost_price) ?? 0;

    const derivedCreatedTime = (() => {
      if (obj.created_time instanceof Date) return obj.created_time;
      const createdAt = obj.created_at ?? obj.createdTime ?? obj.created_time;
      if (!createdAt) return new Date();
      const parsed = new Date(createdAt);
      return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    })();

    const derivedCategory = (() => {
      if (obj.category && typeof obj.category === 'object') {
        const id = String((obj.category as any).id ?? (obj.category as any).slug ?? '');
        const slug = String((obj.category as any).slug ?? (obj.category as any).id ?? '');
        const name = String((obj.category as any).name ?? slug ?? id ?? '');
        return { id, name, slug };
      }
      const slug = String(obj.category ?? '');
      return { id: slug, name: slug, slug };
    })();

    const derivedBrandName = String(obj.brand_name ?? obj.brand ?? '');
    const derivedModelNo = String(obj.model_no ?? '');
    const derivedDescription = obj.description == null ? '' : String(obj.description);
    const derivedRichDescription =
      obj.rich_description == null ? derivedDescription : String(obj.rich_description);

    const derivedStatus = (() => {
      if (obj.status) return obj.status as ProductStatus;
      const stock = String(obj.stock_status ?? '').toLowerCase();
      if (stock === 'out_of_stock') return ProductStatus.OUT_OF_STOCK;
      if (stock === 'discontinued') return ProductStatus.DISCONTINUED;
      return ProductStatus.ACTIVE;
    })();
    const normalizedPssOptions = (obj.pss_options ?? obj.pssOptions) as
      | PssOptions
      | undefined;

    const normalizedCompareAtPrice = getFirstFiniteNumber(
      obj.compare_at_price,
      obj.pricing?.original_price
    );

    return new Product(
      obj.id,
      obj.name,
      String(obj.search_name ?? obj.name ?? ''),
      derivedBrandName,
      String(obj.search_brand_name ?? derivedBrandName ?? ''),
      derivedModelNo,
      derivedDescription,
      derivedRichDescription,
      derivedCostPrice,
      derivedRetailPrice,
      toFiniteNumber(obj.max_payment_period) ?? 0,
      (obj.images ?? []) as ProductImage[],
      (obj.image_url ?? null) as string | null,
      (obj.short_url ?? null) as string | null,
      derivedCategory,
      String(obj.merchant_shop_id ?? ''),
      String(obj.shop_name ?? ''),
      String(obj.shop_logo ?? ''),
      String(obj.lender_id ?? ''),
      Boolean(obj.is_featured ?? false),
      derivedStatus,
      String(obj.postgres_id ?? obj.id),
      derivedCreatedTime,
      obj.tags ?? [],
      normalizedPricing,
      obj.rating_average,
      normalizedCompareAtPrice ?? undefined,
      obj.refurbished_info,
      normalizedPssOptions,
      pricingCurrency,
      obj.variant_summary ?? null,
      obj.variants ?? [],
      obj.is_master_product ?? false,
      obj.variant_attributes ?? null,
      obj.stock_status,
      obj.quantity_available ?? null,
      obj.sellability,
      obj.specifications ?? [],
      obj.expert_advice ?? null,
      obj.social_proof ?? null,
      Boolean(obj.is_favorite ?? false)
    );
  }

  calculateDiscountPercentage(): number {
    const currentPrice =
      toFiniteNumber(this.pricing.current_display_price) ??
      toFiniteNumber(this.pricing.display_price) ??
      this.retail_price;
    const originalPrice =
      toFiniteNumber(this.compare_at_price) ??
      toFiniteNumber(this.pricing.original_price) ??
      currentPrice;

    if (!Number.isFinite(currentPrice) || !Number.isFinite(originalPrice)) {
      return 0;
    }
    if (originalPrice <= 0) {
      return 0;
    }
    if (currentPrice >= originalPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  }

  getDiscountAmount(): number {
    const currentPrice =
      toFiniteNumber(this.pricing.current_display_price) ??
      toFiniteNumber(this.pricing.display_price) ??
      this.retail_price;
    const originalPrice =
      toFiniteNumber(this.compare_at_price) ??
      toFiniteNumber(this.pricing.original_price) ??
      currentPrice;
    return Math.max(0, originalPrice - currentPrice);
  }

  isDiscounted(): boolean {
    if (this.pricing.is_discounted === true) {
      return true;
    }

    const explicitAmount = toFiniteNumber(this.pricing.discount?.amount);
    if (explicitAmount !== null && explicitAmount > 0) {
      return true;
    }

    const explicitPercent = toFiniteNumber(this.pricing.discount?.percent);
    if (explicitPercent !== null && explicitPercent > 0) {
      return true;
    }

    return this.calculateDiscountPercentage() > 0;
  }

  isRefurbished(): boolean {
    return !!this.refurbished_info;
  }

  getPssMonthlyPayment(months: number): number {
    if (!this.isEligibleForPaymentPlan() || months <= 0) return 0;
    return this.retail_price / months;
  }

  getPssWeeklyPayment(weeks: number): number {
    if (!this.isEligibleForPaymentPlan() || weeks <= 0) return 0;
    return this.retail_price / weeks;
  }

  isAvailable(): boolean {
    return this.status === ProductStatus.ACTIVE;
  }

  isEligibleForPaymentPlan(): boolean {
    return this.max_payment_period > 0 && this.isAvailable();
  }

  getFormattedPrice(): string {
    const currency = resolveCurrencyCode(this.pricing.currency, this.currency);

    try {
      return new Intl.NumberFormat('en-GH', {
        style: 'currency',
        currency,
      }).format(this.retail_price);
    } catch {
      return new Intl.NumberFormat('en-GH', {
        style: 'currency',
        currency: 'GHS',
      }).format(this.retail_price);
    }
  }
}

export enum ProductStatus {
  ACTIVE = 'A',
  INACTIVE = 'I',
  OUT_OF_STOCK = 'O',
  DISCONTINUED = 'D',
}

export interface ProductCreateParams {
  id: string;
  name: string;
  brand_name: string;
  model_no: string;
  cost_price: number;
  retail_price: number;
  max_payment_period: number;
  images: ProductImage[];
  categories: string[];
  merchant_shop_id: string;
  shop_name: string;
  shop_logo: string;
  lender_id: string;
  postgres_id: string;
  search_name?: string;
  search_brand_name?: string;
  description?: string;
  rich_description?: string;
  is_featured?: boolean;
  status?: ProductStatus;
  created_time?: Date;
  tags?: string[];
  rating_average?: number;
  compare_at_price?: number;
  refurbished_info?: RefurbishedInfo;
  pss_options?: PssOptions;
  currency?: string;
}

export interface ProductUpdateParams
  extends Partial<Omit<ProductCreateParams, 'id' | 'postgres_id'>> {}

export class Category {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly banner_url: string,
    public readonly active: boolean,
    public readonly slug: string,
    public readonly parent_id?: string,
    public readonly icon_url?: string,
    public readonly product_count?: number,
    public readonly sort_order: number = 0,
    public readonly is_featured: boolean = false,
    public readonly is_featured_menu: boolean = false,
    public readonly children: Category[] = []
  ) {}

  static create(params: CategoryCreateParams): Category {
    return new Category(
      params.id,
      params.name,
      params.banner_url,
      params.active ?? true,
      params.slug || params.name.toLowerCase().replace(/\s+/g, '-'),
      params.parent_id,
      params.icon_url,
      params.product_count,
      params.sort_order || 0,
      params.is_featured || false,
      params.is_featured_menu || false
    );
  }

  isActive(): boolean {
    return this.active;
  }
}

export interface CategoryCreateParams {
  id: string;
  name: string;
  banner_url: string;
  active?: boolean;
  slug?: string;
  parent_id?: string;
  icon_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured?: boolean;
  is_featured_menu?: boolean;
}

export class Store {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly logo: string,
    public readonly image: string,
    public readonly category: string
  ) {}

  static create(params: StoreCreateParams): Store {
    return new Store(
      params.id,
      params.name,
      params.logo,
      params.image,
      params.category
    );
  }
}

export interface StoreCreateParams {
  id: string;
  name: string;
  logo: string;
  image: string;
  category: string;
}

// Brand entity - matches backend ProductBrand model
export interface Brand {
  id: string;
  name: string;
  slug: string;
  logo_url?: string | null;
  website_url?: string | null;
  description?: string | null;
  is_active: boolean;
}

export interface ApiBrandsResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Brand[];
}

export interface HomepageSection {
  key: string;
  title: string;
  description: string;
  products: Product[];
}

export interface HomepageCollections {
  sections: HomepageSection[];
}

export interface MarketplaceHeroBanner {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  badge_text?: string;
  cta_label?: string;
  cta_url?: string;
  image_desktop: string;
  image_mobile?: string;
  supporting_text?: string;
  trailing_text?: string;
  content_kind?: 'campaign' | 'partner' | 'service' | 'generic';
  campaign_priority?: 'critical' | 'high' | 'normal' | 'low';
  program_campaign_id?: string | null;
  promotional_campaign_id?: string | null;
  priority: number;
  rotation_group?: string;
}

export interface MarketplaceShowcaseCard {
  id: string;
  slug: string;
  kind: 'campaign' | 'pay4me' | 'repscore' | 'loyalty' | 'custom';
  badge_text?: string;
  title: string;
  body?: string;
  cta_label?: string;
  cta_url?: string;
  image_url?: string;
  short_url?: string | null;
  background_image_url?: string;
  background_color?: string;
  accent_color?: string;
  metric_label?: string;
  metric_value?: number;
  metric_target?: number;
  sort_order: number;
}

export interface ApiMarketplaceHeroBannerResponse {
  results: MarketplaceHeroBanner[];
}

export interface ApiMarketplaceShowcaseCardResponse {
  results: MarketplaceShowcaseCard[];
}

export interface MarketplaceAnnouncementStrip {
  id: string;
  slug: string;
  message: string;
  detail?: string;
  cta_label?: string;
  mobile_cta_label?: string;
  cta_url?: string;
  priority: 'critical' | 'high' | 'normal' | 'low';
  dismissible: boolean;
  scope: 'global' | 'homepage' | 'merchandising';
  program_campaign_id?: string | null;
  promotional_campaign_id?: string | null;
  sort_order: number;
}

export interface ApiMarketplaceAnnouncementStripResponse {
  results: MarketplaceAnnouncementStrip[];
}

export interface ProgramRewardOffer {
  id: string;
  source_type: string;
  reward_type: string;
  amount?: string | number | null;
  currency?: string;
  claim_event?: string;
  status: string;
  expires_at?: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  program_code?: string;
  program_label?: string;
  reward_deadline_label?: string;
  reward_deadline_window?: string;
  offer_state?: string;
  headline?: string;
  body?: string;
  cta_label?: string;
  landing_page_url?: string;
  fulfillment_mode?: string;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
}

export interface ApiProgramRewardOfferResponse {
  results: ProgramRewardOffer[];
}

export interface ProductCollection {
  id: string;
  name: string;
  description?: string;
  products: Product[];
  filters?: ProductFilters;
}

export interface ProductFilters {
  categories?: string[];
  includeDescendants?: boolean;
  brandName?: string;
  minPrice?: number;
  maxPrice?: number;
  isFeatured?: boolean;
  status?: string;
  page?: number;
  limit?: number;
  offset?: number;
  sortBy?: 'price' | 'name' | 'created' | 'featured';
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  refurbished?: boolean;
  grade?: 'A' | 'B' | 'C';
  warrantyMonths?: number;
}

export interface ProductSearchFilters extends ProductFilters {
  query: string;
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string | null;
  previous?: string | null;
}

export interface ProductApiResponse extends PaginatedResponse<Product> {
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

// API Response Types based on documentation
export interface ApiProductResponse {
  id: string;
  name: string;
  image_url?: string;
  is_favorite?: boolean;
  is_master_product?: boolean;
  variant_attributes?: Record<string, string> | null;
  pricing?: {
    current_display_price?: string | null;
    display_price?: string | null;
    currency?: string | null;
    original_price?: string;
    compare_at_price?: string;
    is_discounted?: boolean;
    discount?: {
      amount: string;
      percent: number;
    };
  } | null;
  sellability?: SellabilityStatus;
  pssOptions?: {
    available: boolean;
    as_low_as_daily?: number;
    as_low_as_weekly?: number;
    as_low_as_monthly?: number;
    popular_plan_months?: number;
  };
  pss_options?: {
    available: boolean;
    as_low_as_daily?: number;
    as_low_as_weekly?: number;
    as_low_as_monthly?: number;
    popular_plan_months?: number;
  };
  stock_status: string;
  quantity_available?: number | null;
  variant_summary?: VariantSummary | null;
  variants?: ProductVariant[];
  specifications?: ProductSpecification[];
  expert_advice?: ProductExpertAdvice | null;
  social_proof?: ProductSocialProof | null;
  rating_average?: number;
  category?: string;
  brand?: string;
  tags?: string[];
  refurbished_info?: {
    grade: 'A' | 'B' | 'C';
    condition: string;
    warranty_months: number;
    includes_accessories: boolean;
  };
  is_featured?: boolean;
  created_at?: string;
}

export interface ApiCategoryResponse {
  id: string;
  slug: string;
  name: string;
  parent_id?: string;
  icon_url?: string;
  banner_image_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured?: boolean;
  is_featured_menu?: boolean;
  children?: ApiCategoryResponse[];
}

export interface ApiHomepageSection {
  key: string;
  title: string;
  description: string;
  view_all_url?: string;
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: ApiProductResponse[];
}

export interface ApiCollectionResponse extends ApiHomepageSection {
  view_all_url: string;
  count: number;
  next: string | null;
  previous: string | null;
}

export interface CollectionPage extends Omit<ApiCollectionResponse, 'results'> {
  results: Product[];
}

export interface ApiHomepageCollections {
  sections: ApiHomepageSection[];
}

export interface ApiPaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

export interface ApiProductsResponse
  extends ApiPaginatedResponse<ApiProductResponse> {
  filtersApplied?: ProductFilters;
  sortApplied?: string;
}

export interface ApiCategoriesResponse
  extends ApiPaginatedResponse<ApiCategoryResponse> {}

export interface ApiFiltersResponse {
  categories?: Array<{
    id: string;
    name: string;
    count: number;
  }>;
  // Backend returns brands as an array of strings (brand names only)
  brands?: Array<string>;
  // Stable identities for the same public catalogue; names need not be unique.
  brand_ids?: Array<string>;
  // Backend returns price_ranges (plural)
  price_ranges?: Array<{
    min: number;
    max: number;
    count: number;
  }>;
  tags?: Array<{
    name: string;
    count: number;
  }>;
  vendor_assignment?: Array<{
    name: string;
    count: number;
  }>;
  refurbished_grades?: Array<{
    grade: string;
    count: number;
  }>;
  warranty_months?: Array<{
    months: string;
    count: number;
  }>;
}

interface ProductPricing {
  display_price: string | null;
  current_display_price: string | null;
  currency: string;
  original_price?: string;
  is_discounted: boolean;
  discount?: {
    amount: string;
    percent: number;
  };
}

// Payment options interfaces (derived from sample JSON)
export interface ProductPaymentOptionsResponse {
  product_id: string;
  currency: string;
  payment_options: PaymentOption[];
}
export interface ProductPaymentPlanResponse {
  product: {
    image_url?: string;
    id: string;
    name: string;
    brand_name?: string;
    model_no?: string;
    pricing: {
      price: string;
      currency: string;
    };
  };
  kmp_policy: {
    available: boolean;
    base_percentage: number;
    min_fee: string;
    currency: string;
    free_lock_period_days?: number; // Days where KMP fee is waived
  };
  user_eligibility: {
    rep_score: number;
    pss_credit_eligible: boolean;
    pss_credit_percentage: number;
    early_redemption_threshold: number;
    recommended_duration: number;
    max_financeable_amount: string;
  };
  duration_options: DurationOption[];
}

export interface DurationOption {
  months: number;
  daily: PaymentFrequency;
  weekly: PaymentFrequency;
  monthly: PaymentFrequency;
}

export interface PaymentFrequency {
  without_kmp: PaymentPlanDetails;
  with_kmp: PaymentPlanDetailsWithKmp;
  schedule?: PaymentSchedule;
}

export interface PaymentPlanDetails {
  period_amount: string;
  total_amount: string;
  installment_count: number;
}

export interface PaymentPlanDetailsWithKmp extends PaymentPlanDetails {
  kmp_fee: string;
  kmp_fee_percentage: number;
}

export interface PaymentSchedule {
  first_due_date: string;
  completion_date: string;
}

export interface PaymentMethod {
  type: 'telecel_mobile_money' | 'mtn_mobile_money' | 'debit_card';
  provider: 'telecel_momo' | 'mtn_momo' | 'paystack_card';
}

export interface PaymentMethodGroups {
  primary: PaymentMethod[];
}

export interface PssCreditEligibility {
  eligible: boolean;
  requires_auth: boolean;
  rep_score: number | null;
  pss_credit_percentage: number | null;
  max_financeable_amount: number | null;
  min_rep_score_required: number | null;
}

export interface PlanPreview {
  tenor_months: number;
  frequency: 'monthly' | string;
  installment_count: number;
  installment_amount: string;
  downpayment_amount: string;
  kmp_fee: string;
  kmp_fee_percentage: number;
  total_payable: string;
  as_low_as_monthly: number;
  as_low_as_weekly: number;
  currency: string;
  early_redemption_eligible: boolean;
}

interface BasePaymentOption {
  type: 'pay_in_full' | 'pay_small_small' | string;
  available: boolean;
  requires_login: boolean;
  payment_methods: PaymentMethodGroups;
  currency: string;
}

export interface PayInFullOption extends BasePaymentOption {
  type: 'pay_in_full';
}

export interface PaySmallSmallOption extends BasePaymentOption {
  type: 'pay_small_small';
  provider: string;
  restricted_payment_method: string;
  pss_credit_eligibility: PssCreditEligibility;
  plan_preview: PlanPreview;
  labels?: string[];
}

export type PaymentOption = PayInFullOption | PaySmallSmallOption;
